import { useContext, useEffect, useRef, useState, useMemo } from 'react';
import PropTypes from 'prop-types';

import * as dayjs from 'dayjs';
import * as utc from 'dayjs/plugin/utc';
import * as timezone from 'dayjs/plugin/timezone';

import { saveAs } from 'file-saver';

import HighchartsWithData from '../HighchartsWithData';

// Types.
import InstanceTarget from '../../types/instance/InstanceTarget';
import ExecutionPlan from '../../types/instance/ExecutionPlan';
import CockroachPlan from '../instance/plan/CockroachPlan';
import Db2Plan from '../instance/plan/Db2Plan';
import MongoPlan from '../instance/plan/MongoPlan';
import MySqlPlan from '../instance/plan/MySqlPlan';
import OraclePlan from '../instance/plan/OraclePlan';
import PostgresPlan from '../instance/plan/PostgresPlan';
import SapHanaPlan from '../instance/plan/SapHanaPlan';
import SqlBatchPlan from '../instance/plan/SqlBatchPlan';
import SybasePlan from '../instance/plan/SybasePlan';
import GenericPlan from '../instance/plan/GenericPlan';
import Statement from "../../types/instance/Statement";
import CombinedChanges from "../../types/instance/CombinedChanges";
import ChartPlotLine from "../../types/ChartPlotLine";
import Time from "../../types/instance/Time";

// Constants.
import {
    INSTANCE_TYPE_COCKROACHDB,
    INSTANCE_TYPE_DB2,
	INSTANCE_TYPE_INFORMIX,
    INSTANCE_TYPE_MONGODB,
    INSTANCE_TYPE_MYSQL,
    INSTANCE_TYPE_ORACLE,
    INSTANCE_TYPE_POSTGRES,
    INSTANCE_TYPE_SAPHANA,
    INSTANCE_TYPE_SQLSERVER,
    INSTANCE_TYPE_SYBASE,
    COLOUR_INSTANCE_EXECUTIONS,
    COLOUR_INSTANCE_TIME
} from '../Constants';

// Helpers.
import { databaseLabel, getConvertedTimeToUTC, getTimeZone } from '../../helpers/utils';
import Helper from "../../helpers/Helper";
import { TimeRangeContext } from "../../context/TimeRangeContext";
import { buildApiQuery, buildRedirectQuery } from "../../helpers/timeRangeUtils";
import { useEventTypes } from "../../context/EventTypeContext";
import ConditionalRender from "../../helpers/ConditionalRender";
import { getAPIString, isGenericStatement, StatementTypeInfo } from "../../views/instances/tabs/utils";
import ChatButton from "../chatGPT/ChatButton";
import Prompts from "../chatGPT/Prompts";
import archiver from "../../api/Archiver";
import EventsApi from "../../api/instance/EventsApi";

dayjs.extend(utc.default);
dayjs.extend(timezone.default);

// Components.
function ExecutionPlans(props: { instance: InstanceTarget, batchId?: string, statementId?: string, setExecutionPlanOptions: Function, statement?: Statement, statementType?: StatementTypeInfo }) {
    const [executionPlans, setExecutionPlans] = useState<ExecutionPlan[]>([]);
    const [executionPlan, setExecutionPlan] = useState<ExecutionPlan>();
    const [combinedChanges, setCombinedChanges] = useState<CombinedChanges[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [showChangeMarkers, setShowChangeMarkers] = useState<boolean>(true);
    const [showCustomEventMarkers, setShowCustomEventMarkers] = useState<boolean>(true);
    const [showAlertMarkers, setShowAlertMarkers] = useState<boolean>(true);
    const [showTotalTimeOverlay, setShowTotalTimeOverlay] = useState<boolean>(false);
    const [showExecutionsOverlay, setShowExecutionsOverlay] = useState<boolean>(false);
    const [showAvgDurationOverlay, setShowAvgDurationOverlay] = useState<boolean>(false);
    const [totalTimeSeries, setTotalTimeSeries] = useState<number[][]>([]);
    const [executionsSeries, setExecutionsSeries] = useState<number[][]>([]);
    const [avgDurationSeries, setAvgDurationSeries] = useState<number[][]>([]);
    const [previousInstanceId, setPreviousInstanceId] = useState<number | undefined>(props.instance.id);
    const timeRangeContext = useContext(TimeRangeContext)
    const { eventTypes } = useEventTypes();
    const { instance } = props;
    const overlayButtonRef = useRef<HTMLButtonElement>(null);

    // Reset stale plan state synchronously when the instance changes, so children
    // never render a plan from a different instance type (e.g. SQL Server XML
    // momentarily passed to MySqlPlan, which would crash JSON.parse).
    if (previousInstanceId !== instance.id) {
        setPreviousInstanceId(instance.id);
        setExecutionPlan(undefined);
        setExecutionPlans([]);
        setLoading(true);
    }

    useEffect(() => {
        const button = overlayButtonRef.current;
        if (!button) return;

        const onShow = () => {
            const panel = button.closest('.p-splitter-panel') as HTMLElement;
            if (panel) panel.style.minHeight = '350px';
        };

        const onHide = () => {
            const panel = button.closest('.p-splitter-panel') as HTMLElement;
            if (panel) panel.style.minHeight = '';
        };

        button.addEventListener('show.bs.dropdown', onShow);
        button.addEventListener('hidden.bs.dropdown', onHide);

        return () => {
            button.removeEventListener('show.bs.dropdown', onShow);
            button.removeEventListener('hidden.bs.dropdown', onHide);
        };
    }, [executionPlans.length]);

    const hasObjectStatistics = Boolean(
            instance?.collectobjectstatistics && instance?.hasobjectstatistics
    );
    const formatCost = (value: number): string =>
            new Intl.NumberFormat('en-GB', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 6
            }).format(value);

    const normalizePoint = (point: any): [number, number] => {
        if (Array.isArray(point)) {
            return [Number(point[0]) || 0, Number(point[1]) || 0];
        }
        return [Number(point?.x) || 0, Number(point?.y) || 0];
    };

    const fillMissingTimeslices = (data: number[][]): number[][] => {
        const from = timeRangeContext.timeRange?.from;
        const to = timeRangeContext.timeRange?.to;
        if (!from || !to) {
            return [...data].sort((a, b) => a[0] - b[0]);
        }

        const interval = Helper.getInterval(from, to, timeRangeContext.timeRange?.interval);
        const start = Math.floor(from.valueOf() / interval) * interval;
        const end = Math.ceil(to.valueOf() / interval) * interval;

        const valueByTimeslice = new Map<number, number>();
        data.forEach((point: any) => {
            const [x, y] = normalizePoint(point);
            const roundedX = Math.floor(x / interval) * interval;
            valueByTimeslice.set(roundedX, y);
        });

        const filledData: number[][] = [];
        for (let timeslice = start; timeslice <= end; timeslice += interval) {
            filledData.push([timeslice, valueByTimeslice.get(timeslice) ?? 0]);
        }
        return filledData;
    };

    useEffect(() => {
        let isActive = true;

        function getTotalCost(sqlplan: string): number {
            let cost: number = 0;
            try {
                // Try to get the first cost value
                // (which should be the accumulative total for the plan).
                switch (instance.type) {
                    case INSTANCE_TYPE_COCKROACHDB:
                        break;

                    case INSTANCE_TYPE_DB2:
                        cost = JSON.parse(sqlplan)[0]['total_cost'];
                        break;

                    case INSTANCE_TYPE_MONGODB:
                        break;

                    case INSTANCE_TYPE_MYSQL:
                        cost = JSON.parse(sqlplan)
                                ['query_block']['cost_info']['query_cost'];
                        break;

                    case INSTANCE_TYPE_ORACLE:
                        cost = JSON.parse(sqlplan)[0]['cost'];
                        break;

                    case INSTANCE_TYPE_POSTGRES:
                        cost = JSON.parse(sqlplan)[0]['Plan']['Total Cost'];
                        break;

                    case INSTANCE_TYPE_INFORMIX:
                        break;

                    case INSTANCE_TYPE_SAPHANA:
                        cost = JSON.parse(sqlplan)[0]['subtree_cost'];
                        break;

                    case INSTANCE_TYPE_SQLSERVER:
                        // Plan is in XML format.
                        break;

                    case INSTANCE_TYPE_SYBASE:
                        break;

                    default:
                        // Unknown instance type.
                        console.error('Unknown instance type.',
                                instance.type);
                        break;
                }
            } catch (error: any) {
                console.warn('Failed to retrieve execution plan cost.', error);
            }

            return Number(cost) || 0;
        }

        const processExecutionPlans = (plans: ExecutionPlan[]) => {
            if (!isActive) return;

            for (let index = 0; index < plans.length; index++) {

                let executionPlan = plans[index],
                        cost: number = 0,
                        valid: boolean = executionPlan.sqlplan !== null;
                const tz = getTimeZone();
                const descriptor = tz === 'UTC'
                        ? dayjs.utc(executionPlan.timeslice).format('DD MMM YYYY HH:mm')
                        : dayjs.default.tz(executionPlan.timeslice, tz).format('DD MMM YYYY HH:mm');

                // Try to find the execution plan cost - this can be hit-and-miss, and varies between instance types.
                if (valid) {
                    cost = getTotalCost(executionPlan.sqlplan);
                }

                // Update the execution plan options.
                executionPlan.planId = index;
                executionPlan.descriptor = descriptor;
                executionPlan.cost = cost;
                executionPlan.valid = valid;

                plans[index] = executionPlan;
            }

            // Filter out plans with no description or plan to explain.
            plans = plans.filter(plan => plan.valid && plan.descriptor && plan.sqlplan !== null);

            if (isActive) {
                setExecutionPlans(plans);
                setLoading(false)

                // Always reflect the current fetch, even when it found nothing - otherwise a plan
                // selected for a previous time range keeps rendering after the range changes.
                setExecutionPlan(plans.length > 0 ? plans[0] : undefined);
                // Return the total execution plan count back to the parent component.
                props.setExecutionPlanOptions.call(null, plans.filter(plan => plan.valid && plan.descriptor));
            }
        }

        const getExecutionPlans = async () => {

            let planFormat: string = '&format=json';

            const apiString = getAPIString(props.statementId || props.batchId, undefined, props.statementType)

            if (props.batchId === undefined && props.statementId === undefined) {
                console.error('Execution plan component requires either a batch or statement ID property to be set.')
                return;
            }

            if (instance.type === INSTANCE_TYPE_SQLSERVER) {
                planFormat = '&format=xml'
            }

            archiver.get(`/rest/v2/sql/plan?${buildApiQuery(timeRangeContext.timeRange)}&sort=timeslice&id=${instance.id}${apiString}${planFormat}`)
                    .then(async (response: { data: ExecutionPlan[]; }) => {
                        if (!isActive) return;
                        let plans: ExecutionPlan[] = response.data;

                        // if it's statement search check also for group plans (DBMAR-1657)
                        const isGeneric = isGenericStatement()
                        if (!plans.length && isGeneric) {
                            // Make another API call with the "grouphash" parameter
                            archiver.get(`/rest/v2/sql/plan?${buildApiQuery(timeRangeContext.timeRange)}&sort=timeslice&id=${instance.id}&grouphash=${props.statementId}${planFormat}`)
                                    .then(async (response: { data: ExecutionPlan[]; }) => {
                                        if (!isActive) return;
                                        plans = response.data;
                                        processExecutionPlans(plans);
                                        setLoading(false);
                                    })
                                    .catch((error: any) => {
                                        if (!isActive) return;
                                        console.error('Failed to retrieve V2 execution plans with grouphash.', error);
                                        setLoading(false);
                                    });
                        } else {
                            processExecutionPlans(plans);
                            setLoading(false);
                        }
                    })
                    .catch((error: any) => {
                        if (!isActive) return;
                        console.error('Failed to retrieve V2 execution plans.', error);
                        setLoading(false);
                    });
        }

        getExecutionPlans();

        return () => {
            isActive = false;
        };
    }, [instance, props.batchId, props.statementId, timeRangeContext.timeRange.from.valueOf(), timeRangeContext.timeRange.to.valueOf(), timeRangeContext.timeRange.interval]);

    useEffect(() => {
        let isActive = true;

        const getChanges = async () => {
            const allEvents = await EventsApi.getEventsChangesAndAlerts(timeRangeContext, instance.id);
            if (!isActive || !allEvents) return;

            const updatedCombinedChanges: CombinedChanges[] = allEvents.map((currentItem: any, index: number) => ({
                id: index,
                instanceId: currentItem.datasourceid,
                timeslice: currentItem.timeslice,
                [currentItem.event]: currentItem
            }));

            updatedCombinedChanges.sort(function compare(a, b) {
                const aa: number = dayjs.default(a.timeslice).valueOf();
                const bb: number = dayjs.default(b.timeslice).valueOf();
                return bb - aa;
            });

            if (isActive) {
                setCombinedChanges(updatedCombinedChanges);
            }
        };

        void getChanges();

        return () => {
            isActive = false;
        };
    }, [instance.id, timeRangeContext.timeRange.from.valueOf(), timeRangeContext.timeRange.to.valueOf(), timeRangeContext.timeRange.interval]);

    useEffect(() => {
        let isActive = true;

        const getOverlayData = async () => {
            const apiString = getAPIString(props.statementId || props.batchId, undefined, props.statementType);
            const queryString = buildApiQuery(timeRangeContext.timeRange);
            const limit = 501;

            const [timeResult, statisticsResult] = await Promise.allSettled([
                archiver.get(`/rest/v1/activity/time?${queryString}&id=${instance.id}${apiString}&interval=0&limit=${limit}&sort=waittime+desc`),
                archiver.get(`/rest/v2/sql/statistic/time?${queryString}&id=${instance.id}&type=${instance.type}${apiString}&interval=0&limit=${limit}&sort=executions+desc`)
            ]);

            if (!isActive) return;

            if (timeResult.status === 'fulfilled') {
                const totalTimeData: number[][] = (timeResult.value.data as Time[]).map((item: Time) => ([
                    getConvertedTimeToUTC(item),
                    Number(item.waittime) || 0
                ]));
                setTotalTimeSeries(fillMissingTimeslices(totalTimeData));
            } else {
                setTotalTimeSeries([]);
            }

            if (statisticsResult.status === 'fulfilled') {
                const sqlStatisticTime = statisticsResult.value.data || [];

                const executionsData: number[][] = sqlStatisticTime.map((item: any) => ([
                    getConvertedTimeToUTC(item),
                    Number(item.executions) || 0
                ]));

                const avgDurationData: number[][] = sqlStatisticTime.map((item: any) => ([
                    getConvertedTimeToUTC(item),
                    Number(
                            item.avgduration ??
                            ((item.executions || 0) > 0 ? (item.duration || 0) / item.executions : 0)
                    ) || 0
                ]));

                setExecutionsSeries(fillMissingTimeslices(executionsData));
                setAvgDurationSeries(fillMissingTimeslices(avgDurationData));
            } else {
                setExecutionsSeries([]);
                setAvgDurationSeries([]);
            }
        };

        void getOverlayData();

        return () => {
            isActive = false;
        };
    }, [instance.id, instance.type, props.statementId, props.batchId, props.statementType, timeRangeContext.timeRange.from.valueOf(), timeRangeContext.timeRange.to.valueOf(), timeRangeContext.timeRange.interval]);

    const chartPlotLines = useMemo((): ChartPlotLine[] => {
        const interval = Helper.getInterval(
                timeRangeContext.timeRange?.from,
                timeRangeContext.timeRange?.to,
                timeRangeContext.timeRange?.interval
        );
        const chartCategories = Helper.getChartAxisCategories(
                timeRangeContext.timeRange?.from,
                timeRangeContext.timeRange?.to,
                interval
        );

        const filteredCombinedChanges = combinedChanges.filter(item => {
            if (item.change && !showChangeMarkers) return false;
            if (item.custom && !showCustomEventMarkers) return false;
            if (item.alert && !showAlertMarkers) return false;
            return true;
        });

        const eventId = props.statementId || (props.batchId ? `${props.statementType?.type || 'batch'}:${props.batchId}` : undefined);
        return Helper.getChartPlotLines(
                buildRedirectQuery(timeRangeContext.timeRange),
                filteredCombinedChanges,
                chartCategories,
                interval,
                false,
                eventId,
                eventTypes
        );
    }, [
        combinedChanges,
        showChangeMarkers,
        showCustomEventMarkers,
        showAlertMarkers,
        timeRangeContext,
        props.statementId,
        props.batchId,
        props.statementType,
        eventTypes
    ]);

    function selectPreviousPlan() {
        if (currentPlanIndex > 0) {
            setExecutionPlan(executionPlans[currentPlanIndex - 1]);
        }
    }

    function selectNextPlan() {
        if (currentPlanIndex !== -1 &&
                currentPlanIndex < executionPlans.length - 1) {
            setExecutionPlan(executionPlans[currentPlanIndex + 1]);
        }
    }

    function selectPreviousPlanEnabled() {
        return currentPlanIndex > 0;
    }

    function selectNextPlanEnabled() {
        return currentPlanIndex !== -1 &&
                currentPlanIndex < executionPlans.length - 1;
    }

    const currentPlanIndex = executionPlan ?
            executionPlans.findIndex(plan => plan.planId === executionPlan.planId) : -1;

    function showPlan() {
        // Keep the compiler happy: the execution plan should not be null here.
        if (!executionPlan) return (<></>);

        switch (instance.type) {
            case INSTANCE_TYPE_COCKROACHDB:
                return (<CockroachPlan hasObjectStatistics={hasObjectStatistics} executionPlan={executionPlan}/>);

            case INSTANCE_TYPE_DB2:
                return (<Db2Plan hasObjectStatistics={hasObjectStatistics} executionPlan={executionPlan}/>);

            case INSTANCE_TYPE_MONGODB:
                return (<MongoPlan hasObjectStatistics={hasObjectStatistics} executionPlan={executionPlan}/>);

            case INSTANCE_TYPE_MYSQL:
                return (<MySqlPlan hasObjectStatistics={hasObjectStatistics} executionPlan={executionPlan}/>);

            case INSTANCE_TYPE_ORACLE:
                return (<OraclePlan hasObjectStatistics={hasObjectStatistics} executionPlan={executionPlan}/>);

            case INSTANCE_TYPE_POSTGRES:
                return (<PostgresPlan hasObjectStatistics={hasObjectStatistics} executionPlan={executionPlan}/>);

            case INSTANCE_TYPE_SAPHANA:
                return (<SapHanaPlan hasObjectStatistics={hasObjectStatistics} executionPlan={executionPlan}/>);

            case INSTANCE_TYPE_SQLSERVER:
                return (<SqlBatchPlan executionPlan={executionPlan}/>);

            case INSTANCE_TYPE_SYBASE:
                return (<SybasePlan hasObjectStatistics={hasObjectStatistics} executionPlan={executionPlan}/>);

            default:
                return (<GenericPlan executionPlan={executionPlan}/>);
        }
    }

    const chartOptions = useMemo(() => {
        if (!executionPlans.length) return {};

        const data = executionPlans.map(plan => {
            const selected = executionPlan &&
                    plan.planId === executionPlan.planId;
            const costNum =
                    plan.cost !== undefined && plan.cost !== null
                            ? Number(plan.cost)
                            : NaN;
            const y = Number.isFinite(costNum) ? costNum : 1;
            return {
                x: getConvertedTimeToUTC(plan),
                y,
                plan,
                name: plan.descriptor,
                marker: {
                    fillColor: selected ? '#007bff' : 'rgba(125, 125, 140, 0.7)',
                    radius: selected ? 6 : 4,
                    lineColor: selected ? '#ffffff' : 'transparent',
                    lineWidth: selected ? 2 : 0
                }
            };
        });

        const overlaySeries: any[] = [];

        if (showTotalTimeOverlay) {
            overlaySeries.push({
                id: 'overlay-total-time',
                type: 'column',
                color: COLOUR_INSTANCE_TIME,
                data: totalTimeSeries.map((point: any) => normalizePoint(point)),
                name: 'Total Time',
                yAxis: 1,
                pointPlacement: 'on',
                zIndex: 1
            });
        }

        if (showExecutionsOverlay) {
            overlaySeries.push({
                id: 'overlay-executions',
                type: 'line',
                color: COLOUR_INSTANCE_EXECUTIONS,
                data: executionsSeries.map((point: any) => normalizePoint(point)),
                name: 'Executions',
                yAxis: 2,
                lineWidth: 1.5,
                marker: { enabled: false },
                zIndex: 1
            });
        }

        if (showAvgDurationOverlay) {
            overlaySeries.push({
                id: 'overlay-avg-duration',
                type: 'line',
                color: '#03bafc',
                data: avgDurationSeries.map((point: any) => normalizePoint(point)),
                name: 'Avg Duration',
                yAxis: 3,
                lineWidth: 1.5,
                marker: { enabled: false },
                zIndex: 1
            });
        }
        const rangeFrom = timeRangeContext.timeRange.from;
        const rangeTo = timeRangeContext.timeRange.to;
        const rangeInterval = Helper.getInterval(rangeFrom, rangeTo, timeRangeContext.timeRange.interval);
        const xMin = rangeFrom ? Math.floor(dayjs.default(rangeFrom).valueOf() / rangeInterval) * rangeInterval : undefined;
        const xMax = rangeTo ? Math.ceil(dayjs.default(rangeTo).valueOf() / rangeInterval) * rangeInterval : undefined;

        return {
            chart: {
                type: 'scatter',
                height: 200,
                marginTop: 18,
                spacing: [15, 10, 15, 10],
                style: {
                    fontFamily: 'system-ui, Arial, sans-serif',
                    letterSpacing: 'normal'
                }
            },
            time: {
                useUTC: getTimeZone() === 'UTC'
            },
            title: {
                text: null
            },
            xAxis: {
                type: 'datetime',
                title: { text: null },
                min: xMin,
                max: xMax,
                lineColor: '#CCCCCC',
                tickColor: '#CCCCCC',
                labels: {
                    style: {
                        color: '#666666',
                        cursor: 'default',
                        fontSize: '11px',
                        letterSpacing: '1px'
                    }
                },
                crosshair: {
                    width: 15,
                    color: 'rgba(125, 160, 200, 0.15)'
                },
                plotLines: chartPlotLines.map((chartPlotLine: ChartPlotLine) => ({
                    ...chartPlotLine,
                    value: chartPlotLine.timeValue,
                    label: {
                        ...chartPlotLine.label,
                        y: 18
                    }
                }))
            },
            yAxis: [{
                title: { text: null },
                min: 0,
                labels: { enabled: false }
            }, {
                title: { text: null },
                labels: { enabled: false },
                min: 0,
                opposite: true
            }, {
                title: { text: null },
                labels: { enabled: false },
                min: 0,
                opposite: true
            }, {
                title: { text: null },
                labels: { enabled: false },
                min: 0,
                opposite: true
            }],
            legend: {
                enabled: overlaySeries.length > 0,
                align: 'center',
                backgroundColor: 'transparent',
                floating: false,
                itemMarginTop: 0,
                itemMarginBottom: 2,
                maxHeight: 70,
                itemStyle: {
                    fontSize: '12px',
                    fontWeight: 'bold',
                    letterSpacing: 'normal',
                    fontFamily: 'system-ui, Arial, sans-serif'
                },
                layout: 'horizontal',
                padding: 0,
                verticalAlign: 'bottom'
            },
            exporting: {
                enabled: false
            },
            tooltip: {
                useHTML: true,
                outside: true,
                shared: false,
                borderWidth: 0,
                backgroundColor: 'none',
                shadow: false,
                padding: 0,
                className: 'execution-plan-point-tooltip',
                formatter: function (this: any) {
                    const tz = getTimeZone();
                    const formattedTime = tz === 'UTC'
                        ? dayjs.utc(this.x).format('DD MMM YYYY HH:mm')
                        : dayjs.default(this.x).tz(tz).format('DD MMM YYYY HH:mm');

                    let rows = '';

                    if (this.series.name === 'Plans') {
                        // Hovering a specific plan point — show only that plan's cost.
                        const plan = this.point.plan;
                        const costPart = `<span style="padding-left:20px" class="value">${plan && plan.cost !== undefined && plan.cost !== null ? formatCost(plan.cost) : '0'}</span>`;
                        rows += `<div style="margin-top:4px"><div class="d-flex justify-content-between"><div><div class="dot" style="background-color:#007bff"></div>Cost:</div>${costPart}</div></div>`;
                    } else {
                        // Hovering a column / line — show all overlay series at this x.
                        const chart = this.series.chart;
                        for (const series of chart.series) {
                            if (series.name === 'Plans') continue;
                            const point = series.points.find((p: any) => p.x === this.x);
                            if (!point) continue;
                            const color = series.color;
                            if (series.name === 'Executions') {
                                rows += `<div class="d-flex justify-content-between"><div><div class="dot" style="background-color:${color}"></div>${series.name}:</div><span style="padding-left:20px" class="value">${Helper.getFormattedNumber(point.y)}</span></div>`;
                            } else if (point.y > 0) {
                                rows += `<div class="d-flex justify-content-between"><div><div class="dot" style="background-color:${color}"></div>${series.name}:</div><span style="padding-left:20px" class="value">${Helper.getTimeInEnglish(point.y)}</span></div>`;
                            }
                        }

                        // Also show all plans that fall in this column.
                        const plansAtX = executionPlans.filter(plan => getConvertedTimeToUTC(plan) === this.x);
                        if (plansAtX.length) {
                            rows += `<div style="margin-top:4px;border-top:1px solid #eee;padding-top:4px">`;
                            for (const plan of plansAtX) {
                                const costPart = `<span style="padding-left:20px" class="value">${plan.cost !== undefined && plan.cost !== null ? formatCost(plan.cost) : '0'}</span>`;
                                rows += `<div class="d-flex justify-content-between"><div><div class="dot" style="background-color:#007bff"></div>Cost:</div>${costPart}</div>`;
                            }
                            rows += `</div>`;
                        }
                    }

                    if (!rows) return false;
                    return `<div class="chart-tooltip-new"><div class="tooltip-header">${formattedTime}</div>${rows}</div>`;
                }
            },
            plotOptions: {
                animation: false,
                column: {
                    stacking: 'normal',
                    pointPlacement: 'on'
                },
                series: {
                    cursor: 'pointer',
                    point: {
                        events: {
                            click: function (this: any) {
                                if (this.plan) {
                                    setExecutionPlan(this.plan);
                                }
                            }
                        }
                    }
                }
            },
            series: [{
                id: 'plan-samples',
                type: 'scatter',
                name: 'Plans',
                showInLegend: false,
                data: data.map((point: any) => ({ ...point })),
                zIndex: 2
            }, ...overlaySeries]
        };
    }, [
        executionPlans,
        executionPlan,
        timeRangeContext.timeRange,
        chartPlotLines,
        showChangeMarkers,
        showCustomEventMarkers,
        showAlertMarkers,
        showTotalTimeOverlay,
        showExecutionsOverlay,
        showAvgDurationOverlay,
        totalTimeSeries,
        executionsSeries,
        avgDurationSeries
    ]);

    // Function to trigger the json download
    function downloadJson() {
        const isJSONFormat = (value: any) => {
            try {
                JSON.parse(value);
                return true;
            } catch (error) {
                return false;
            }
        };
        if (executionPlan) {
            const isJSON = isJSONFormat(executionPlan.sqlplan);
            if (isJSON) {
                const blob = new Blob([executionPlan.sqlplan], { type: 'application/json' });
                saveAs(blob, `${instance.name} - ${((props.batchId !== undefined) ? props.batchId : props.statementId)} - Execution Plan - ${executionPlan.descriptor}.json`);
            } else {
                // Display the text content with a monospaced font in a <pre> tag
                const executionPlanText = executionPlan.sqlplan
                const preTag = document.createElement('pre');

                // Not sure why we need styling for a txt file... https://appperf.atlassian.net/browse/DBMAR-1536
                preTag.style.fontSize = '14px';
                preTag.style.fontFamily = 'Courier New, Courier, monospace';
                preTag.style.display = 'none';
                preTag.textContent = executionPlanText;
                document.body.appendChild(preTag);

                const blob = new Blob([executionPlanText], { type: 'text/plain;charset=utf-8' });
                saveAs(blob, `${instance.name} - ${((props.batchId !== undefined) ? props.batchId : props.statementId)} - Execution Plan - ${executionPlan.descriptor}.txt`);
            }
        }
    }

    function downloadSqlServerPlan() {
        if (!executionPlan?.sqlplan) return;

        const blob = new Blob([executionPlan.sqlplan], { type: 'application/xml;charset=utf-8' });
        saveAs(
            blob,
            `${instance.name} - ${((props.batchId !== undefined) ? props.batchId : props.statementId)} - Execution Plan - ${executionPlan.descriptor}.sqlplan`
        );
    }

    return (
            <div id="plans" className="tab-pane" role="tabpanel" aria-labelledby="plans-tab">
                <div>
                    <div className="card-header sticky-card-header">
                        <div className="d-flex flex-column flex-md-row flex-md-wrap align-items-stretch align-items-md-center gap-2 w-100">
                            <div className="d-flex align-items-center flex-wrap gap-2 flex-grow-1 min-w-0">
                                <i className="fal fa-ruler-triangle fa-fw" aria-hidden="true"/>
                                <span className="me-1">Execution Plans</span>
                                {executionPlans.length > 1 && (
                                        <div className="btn-group">
                                            <button
                                                    type="button"
                                                    className="btn btn-xsm btn-outline-secondary"
                                                    onClick={selectPreviousPlan}
                                                    disabled={!selectPreviousPlanEnabled()}
                                                    title="Previous Plan"
                                            >
                                                <i className="fal fa-arrow-left"/>
                                            </button>
                                            <button
                                                    type="button"
                                                    className="btn btn-xsm btn-outline-secondary"
                                                    onClick={selectNextPlan}
                                                    disabled={!selectNextPlanEnabled()}
                                                    title="Next Plan"
                                            >
                                                <i className="fal fa-arrow-right"/>
                                            </button>
                                        </div>
                                )}
                                {executionPlans.length > 0 && (
                                        <div className="btn-group">
                                            <button
                                                    ref={overlayButtonRef}
                                                    id="executionPlanOverlay"
                                                    type="button"
                                                    className="btn btn-xsm btn-outline-secondary dropdown-toggle"
                                                    data-bs-toggle="dropdown"
                                                    data-bs-auto-close="outside"
                                                    data-bs-boundary="viewport"
                                                    aria-expanded="false"
                                            >
                                                Overlay
                                            </button>
                                            <ul className="dropdown-menu border" aria-labelledby="executionPlanOverlay" style={{ userSelect: 'none' }}>
                                                <li>
                                            <span className="dropdown-item" role="button" onClick={() => setShowChangeMarkers(!showChangeMarkers)}>
                                                <i className={`fal fa-check fa-fw me-1 ${showChangeMarkers ? '' : 'invisible'}`}/>
                                                Changes
                                            </span>
                                                </li>
                                                <li>
                                            <span className="dropdown-item" role="button" onClick={() => setShowCustomEventMarkers(!showCustomEventMarkers)}>
                                                <i className={`fal fa-check fa-fw me-1 ${showCustomEventMarkers ? '' : 'invisible'}`}/>
                                                Custom events
                                            </span>
                                                </li>
                                                <li>
                                            <span className="dropdown-item" role="button" onClick={() => setShowAlertMarkers(!showAlertMarkers)}>
                                                <i className={`fal fa-check fa-fw me-1 ${showAlertMarkers ? '' : 'invisible'}`}/>
                                                Alerts
                                            </span>
                                                </li>
                                                <li>
                                                    <hr className="dropdown-divider"/>
                                                </li>
                                                <li>
                                            <span className="dropdown-item" role="button" onClick={() => setShowTotalTimeOverlay(!showTotalTimeOverlay)}>
                                                <i className={`fal fa-check fa-fw me-1 ${showTotalTimeOverlay ? '' : 'invisible'}`}/>
                                                Total Time
                                            </span>
                                                </li>
                                                <li>
                                            <span className="dropdown-item" role="button" onClick={() => setShowExecutionsOverlay(!showExecutionsOverlay)}>
                                                <i className={`fal fa-check fa-fw me-1 ${showExecutionsOverlay ? '' : 'invisible'}`}/>
                                                Executions
                                            </span>
                                                </li>
                                                <li>
                                            <span className="dropdown-item" role="button" onClick={() => setShowAvgDurationOverlay(!showAvgDurationOverlay)}>
                                                <i className={`fal fa-check fa-fw me-1 ${showAvgDurationOverlay ? '' : 'invisible'}`}/>
                                                Avg Duration
                                            </span>
                                                </li>
                                            </ul>
                                        </div>
                                )}
                                <i className="collapse-toggle ms-auto" role="button" data-bs-toggle="collapse"
                                   data-bs-target="#collapsePlans" aria-expanded="false" aria-controls="collapsePlans"/>
                            </div>
                            <div className="d-flex flex-wrap gap-2 align-items-center ms-md-auto" role="group">
                                <ConditionalRender if={props.statement && executionPlan?.sqlplan}>
                                    {/*@ts-ignore*/}
                                    <ChatButton value={Prompts.getStatementTextPrompt(props.statement, executionPlan?.sqlplan.toString(), instance.type)}/>
                                </ConditionalRender>
                                {executionPlan && (
                                    <>
                                        <ConditionalRender if={instance.type === INSTANCE_TYPE_SQLSERVER}>
                                            <button type="button" onClick={downloadSqlServerPlan} className="btn btn-xsm btn-primary">
                                                Download Plan
                                            </button>
                                        </ConditionalRender>
                                        <ConditionalRender if={instance.type !== INSTANCE_TYPE_SQLSERVER}>
                                            <button type="button" onClick={downloadJson} className="btn btn-xsm btn-primary">
                                                Download Plan
                                            </button>
                                        </ConditionalRender>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                    <div id="collapsePlans" className="card-body collapse show">
                        <ConditionalRender if={loading}>
                            <div className="w-100 text-center text-muted mt-3">
                                <div className="loader spinner chartSpinner">
                                </div>
                                <p className="mt-3">
                                    Loading data...
                                </p>
                            </div>
                        </ConditionalRender>

                        <ConditionalRender if={!loading && executionPlan === undefined}>
                            <div className="w-100 text-center text-muted my-3">
                                <i className="fal fa-ban fa-fw fa-2x text-muted"/>
                                <p>No Execution Plans Found</p>
                            </div>
                        </ConditionalRender>
                        <ConditionalRender if={!loading && executionPlans.length > 0}>
                            <div style={{ marginBottom: '20px', position: 'relative', zIndex: 0 }}>
                                <div style={{ paddingLeft: '10px' }}>
                                    <h6 style={{ margin: '0 0 5px 0' }}>Execution plans</h6>
                                    <p style={{ fontSize: '13px', color: '#666', margin: 0 }}>
                                        Select one of the dots to see the execution plan.
                                    </p>
                                </div>
                                <div className="execution-plan-samples-chart">
                                    <HighchartsWithData
                                        error={null}
                                        loading={loading}
                                        length={executionPlans.length}
                                        options={chartOptions}
                                    />
                                </div>
                            </div>
                        </ConditionalRender>
                        <ConditionalRender if={!loading && executionPlan}>
                            <div className='execution-plan'>
                                <p>The execution plan below is for the period <span
                                        className="fw-bold">{executionPlan?.descriptor}
                            </span> for the {databaseLabel(instance.type)} <span
                                        className="fw-bold">{executionPlan?.database}
                                </span> and username <span className="fw-bold">{executionPlan?.username}</span>.
                                </p>
                                {showPlan()}
                            </div>
                        </ConditionalRender>
                    </div>
                </div>
            </div>
    );
}

ExecutionPlans.propTypes = {
    setExecutionPlanOptions: PropTypes.func.isRequired
};

export default ExecutionPlans;
